import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "5.0";

  return (
    <div data-testid="reviews-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50 grid-pattern">
        <div className="container-custom text-center">
          <div className="flex items-center justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 font-montserrat">
            Customer Reviews
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            {averageRating} average rating from {reviews.length}+ satisfied customers
          </p>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-50 rounded-2xl p-8 space-y-4">
                  <div className="h-4 skeleton rounded w-1/2" />
                  <div className="h-20 skeleton rounded" />
                  <div className="h-4 skeleton rounded w-1/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review, index) => (
                <div 
                  key={review.id}
                  className="review-card"
                  data-testid={`review-card-${index}`}
                >
                  <Quote className="w-10 h-10 text-primary/20 mb-4" />
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    "{review.text}"
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{review.author}</p>
                      {review.project_type && (
                        <p className="text-sm text-gray-500 capitalize">{review.project_type} project</p>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      Verified Customer
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Elfsight Reviews Widget (Google reviews via Elfsight embed) */}
          <ElfsightReviews />
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-montserrat">
            Join Our Satisfied Customers
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Experience the quality and service that our customers rave about.
          </p>
          <Link to="/book">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100" data-testid="reviews-final-cta">
              Book My Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

const ElfsightReviews = () => {
  const widgetId = process.env.REACT_APP_ELFSIGHT_REVIEWS_WIDGET_ID;

  useEffect(() => {
    if (!widgetId) return;
    const existing = document.querySelector('script[src="https://elfsightcdn.com/platform.js"]');
    if (existing) return;
    const s = document.createElement("script");
    s.src = "https://elfsightcdn.com/platform.js";
    s.async = true;
    document.body.appendChild(s);
  }, [widgetId]);

  if (!widgetId) {
    return (
      <div className="mt-12 text-center">
        <div className="bg-gray-50 rounded-2xl p-8 max-w-xl mx-auto" data-testid="elfsight-placeholder">
          <p className="text-gray-600">
            Live reviews widget will appear here once configured.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Set <code>REACT_APP_ELFSIGHT_REVIEWS_WIDGET_ID</code> in frontend/.env to enable.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12" data-testid="elfsight-reviews-widget">
      <div className={`elfsight-app-${widgetId}`} data-elfsight-app-lazy />
    </div>
  );
};

export default ReviewsPage;
